import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';

import { useSettings } from '../context/SettingsContext'
import { Nav } from './partials/Nav';
import { API_URL } from './Constants';

const SalesReport = () => {
  const settings = useSettings();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState(null);
  
  // Initialize dates to current month
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
  });

  useEffect(() => {
    document.title = 'Sales Report | ValParx Admin'
  }, []);

  useEffect(() => {
    if (settings.userToken) {
      fetchReport();
    }
  }, [settings.userToken]);

  const fetchReport = () => {
    setLoading(true);
    setError(null);
    
    const token = settings.userToken;
    const myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);
    myHeaders.append("Content-Type", "application/x-www-form-urlencoded");

    const urlencoded = new URLSearchParams();
    urlencoded.append("start_date", startDate);
    urlencoded.append("end_date", endDate);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: urlencoded,
      redirect: 'follow'
    };

    fetch(API_URL + "/getsalesreport", requestOptions)
      .then(response => response.json())
      .then(result => {
        if (result.code === 200) {
          setReportData(result);
        } else {
          setError(result.message || 'Failed to fetch report');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('error', error);
        setError('Failed to fetch report data');
        setLoading(false);
      });
  };

  const handleGenerateReport = (e) => {
    e.preventDefault();
    fetchReport();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const exportToCSV = () => {
    if (!reportData || !reportData.lots || reportData.lots.length === 0) return;

    const headers = ['Lot Name', 'Total Transactions', 'Regular Transactions', 'Monthly Parkers', 'Total Sales', 'Fees Collected'];

    const rows = reportData.lots.map(lot => [
      lot.lot_name,
      lot.total_transactions,
      lot.regular_transaction_count,
      lot.monthly_parker_count,
      formatCurrency(lot.total_sales),
      formatCurrency(lot.total_fees),
    ]);

    // Add totals row
    rows.push([
      'TOTALS',
      reportData.totals.total_transactions,
      reportData.totals.regular_transaction_count,
      reportData.totals.monthly_parker_count,
      formatCurrency(reportData.totals.total_sales),
      formatCurrency(reportData.totals.total_fees),
    ]);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sales-report-${startDate}-to-${endDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const hasExportData = reportData && reportData.lots && reportData.lots.length > 0;

  return (
    <div className='min-h-screen flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <Nav />
      <div className="max-w-7xl w-full space-y-8 pt-20">
        <div className='flex justify-between items-center mb-8'>
          <div>
            <Link to="/reports" className="text-accent hover:text-accent-dark mb-2 inline-flex items-center">
              <FontAwesomeIcon icon="fa-arrow-left fa-solid" className='mr-2 text-sm' />
              Back to Reports
            </Link>
            <h2 className='mb-0'>General Sales Report</h2>
          </div>
          <div>
            <button
              onClick={exportToCSV}
              disabled={!hasExportData}
              className="px-4 py-2 bg-accent text-white font-medium rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              <FontAwesomeIcon icon="fa-solid fa-file-csv" className='mr-2' />
              Export CSV
            </button>
          </div>
        </div>

        {/* Date Range Pickers */}
        <form onSubmit={handleGenerateReport} className="bg-white shadow-md rounded-lg p-6 mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                id="start_date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label htmlFor="end_date" className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                id="end_date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-accent text-white font-medium rounded-md hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon="fa-solid fa-spinner" className='mr-2 fa-spin' />
                    Loading...
                  </>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>
          </div>
        </form>

        {/* Error Display */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {/* Report Table */}
        {reportData && !loading && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Report Period: {new Date(reportData.start_date).toLocaleDateString()} - {new Date(reportData.end_date).toLocaleDateString()}
              </h3>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Lot Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Transactions
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Regular Transactions
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Monthly Parkers
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Sales
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fees Collected
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.lots.length > 0 ? (
                    <>
                      {reportData.lots.map((lot, index) => (
                        <tr key={lot.lot_id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {lot.lot_name}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {lot.total_transactions}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {lot.regular_transaction_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                            {lot.monthly_parker_count}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-semibold">
                            {formatCurrency(lot.total_sales)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {formatCurrency(lot.total_fees)}
                          </td>
                        </tr>
                      ))}
                      {/* Totals Row */}
                      <tr className="bg-gray-100 font-bold">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          TOTALS
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {reportData.totals.total_transactions}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {reportData.totals.regular_transaction_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {reportData.totals.monthly_parker_count}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(reportData.totals.total_sales)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                          {formatCurrency(reportData.totals.total_fees)}
                        </td>
                      </tr>
                    </>
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                        No transactions found for the selected date range.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Legend */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> Regular Transactions include credit card and cash payments. 
                Monthly Parkers are customers with monthly parking passes and do not contribute to the sales total.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SalesReport;
